import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1655841439659-0afc60676b70?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzZ8MHwxfHNlYXJjaHw0fHxhYnN0cmFjdCUyMDNkJTIwZGFyayUyMGdsYXNzfGVufDB8fHx8MTc5MzAwNTUwNnww&ixlib=rb-4.1.0&q=85";

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, delay: 0.12 * i, ease: [0.22, 1, 0.36, 1] },
  }),
};

export const Hero = () => (
  <section
    id="top"
    data-testid="hero-section"
    className="grain relative overflow-hidden pt-36 pb-20 md:pt-44"
  >
    <div
      aria-hidden="true"
      className="pointer-events-none absolute inset-0"
      style={{
        background:
          "radial-gradient(52% 40% at 50% 0%, rgba(35,60,77,0.55) 0%, rgba(11,16,13,0) 70%), radial-gradient(30% 26% at 82% 18%, rgba(255,138,76,0.10) 0%, rgba(11,16,13,0) 70%), radial-gradient(28% 24% at 14% 22%, rgba(188,232,209,0.07) 0%, rgba(11,16,13,0) 70%)",
      }}
    />

    <div className="relative mx-auto flex max-w-7xl flex-col items-center px-6 text-center md:px-12">
      <motion.h1
        variants={fadeUp}
        initial="hidden"
        animate="show"
        custom={0}
        data-testid="hero-headline"
        className="max-w-4xl text-5xl font-semibold leading-[1.04] tracking-tighter text-[#f3f5f3] md:text-6xl lg:text-7xl"
      >
        Search so good
        <br />
        it feels like{" "}
        <span className="glint relative inline-block overflow-hidden rounded-lg bg-gradient-to-r from-[#ff8a4c] via-[#ffc42e] to-[#ff8a4c] bg-clip-text italic text-transparent">
          magic
        </span>
        .
      </motion.h1>

      <motion.p
        variants={fadeUp}
        initial="hidden"
        animate="show"
        custom={1}
        data-testid="hero-subline"
        className="mt-6 max-w-xl text-base leading-relaxed text-[#a7b1b5] md:text-lg"
      >
        All of your data, instantly searchable by AI.
        <br className="hidden sm:block" /> Search at the speed of thought.
      </motion.p>

      <motion.div
        variants={fadeUp}
        initial="hidden"
        animate="show"
        custom={2}
        className="mt-9 flex flex-col items-center gap-3 sm:flex-row"
      >
        <a
          href="https://app.wondersearch.ai/"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="hero-explore-btn"
          className="inline-flex items-center gap-2 rounded-full bg-[#f3f5f3] px-7 py-3.5 text-sm font-semibold text-[#0b100d] transition-all duration-200 hover:-translate-y-0.5 hover:bg-white hover:shadow-[0_10px_36px_rgba(243,245,243,0.28)]"
        >
          Explore WonderSearch <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
        </a>
        <a
          href="https://cal.com/wondersearch/15min"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="hero-book-call-btn"
          className="inline-flex items-center gap-2 rounded-full border border-[#233c4d]/60 px-7 py-3.5 text-sm font-semibold text-[#edf2f4] transition-all duration-200 hover:-translate-y-0.5 hover:border-[#bce8d1]/50 hover:bg-[#111813]"
        >
          Book a call <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
        </a>
      </motion.div>

      <motion.div
        variants={fadeUp}
        initial="hidden"
        animate="show"
        custom={3}
        data-testid="hero-yc-badge"
        className="mt-8 flex items-center gap-2 text-sm text-[#889296]"
      >
        <span>Backed by</span>
        <a
          href="https://www.ycombinator.com/"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="hero-yc-link"
          aria-label="Y Combinator"
          className="inline-flex items-center gap-1.5 font-medium text-[#c3cbce] transition-colors duration-200 hover:text-[#edf2f4]"
        >
          <span className="grid h-5 w-5 place-items-center rounded-[4px] bg-[#ff6b2c] text-[11px] font-bold text-white">
            Y
          </span>
          Combinator
        </a>
      </motion.div>

      <motion.div
        variants={fadeUp}
        initial="hidden"
        animate="show"
        custom={4}
        className="relative mt-16 w-full max-w-4xl"
      >
        <div
          aria-hidden="true"
          className="absolute -inset-8 rounded-[40px] opacity-70 blur-3xl"
          style={{
            background:
              "radial-gradient(45% 55% at 50% 45%, rgba(255,138,76,0.16) 0%, rgba(35,60,77,0.25) 55%, rgba(11,16,13,0) 100%)",
          }}
        />
        <div className="animate-float-slow relative overflow-hidden rounded-3xl border border-[#233c4d]/40 shadow-[0_24px_80px_rgba(0,0,0,0.5)]">
          <img
            src={HERO_IMG}
            alt="Abstract dark 3D glass object — the WonderSearch signature product visual"
            data-testid="hero-product-visual"
            className="h-auto w-full object-cover"
            loading="eager"
          />
          <div
            aria-hidden="true"
            className="absolute inset-0 bg-gradient-to-t from-[#0b100d]/60 via-transparent to-transparent"
          />
        </div>
      </motion.div>
    </div>
  </section>
);
